import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Navbar from "@/components/Navbar";
import QuickStartGuide from "@/components/QuickStartGuide";
import VideoPlayer from "@/components/VideoPlayer";
import { Share2, Copy, Check, ExternalLink, Play, Pencil, Crown, Megaphone, Calculator, Cog, Download } from "lucide-react";
import CapstoneTracker from "@/components/CapstoneTracker";
import LemonBotFloater from "@/components/LemonBotFloater";
import StudentProfileModal from "@/components/StudentProfileModal";
import AnnouncementBanner from "@/components/AnnouncementBanner";

// Avatar preset emoji map — duplicated from the modal so the dashboard
// can render the chosen avatar without a network roundtrip. Backend remains
// source-of-truth (it validates that avatar_url is a known preset key).
const AVATAR_EMOJI = {
  lemon: "🍋", rocket: "🚀", star: "⭐", sparkles: "✨",
  fire: "🔥", lightning: "⚡", globe: "🌍", leaf: "🌱",
  trophy: "🏆", target: "🎯", rainbow: "🌈", music: "🎵",
};
const AVATAR_BG = {
  lemon: "#FEF3C7", rocket: "#DBEAFE", star: "#FEF9C3", sparkles: "#FCE7F3",
  fire: "#FFEDD5", lightning: "#FEF3C7", globe: "#D1FAE5", leaf: "#DCFCE7",
  trophy: "#FEF3C7", target: "#FEE2E2", rainbow: "#F3E8FF", music: "#E0E7FF",
};
const TEAM_ROLE_META = {
  leader: { label: "Leader", icon: Crown, color: "bg-purple-100 text-purple-700 border-purple-300" },
  marketing: { label: "Marketing", icon: Megaphone, color: "bg-pink-100 text-pink-700 border-pink-300" },
  finance: { label: "Finance", icon: Calculator, color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  operations: { label: "Operations", icon: Cog, color: "bg-blue-100 text-blue-700 border-blue-300" },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Welcome tutorial video URL
const WELCOME_VIDEO_URL = "https://customer-assets.emergentagent.com/job_learntrepreneurs/artifacts/aevrmtac_The%20Lemonade%20Stand%20-%20Student%20Dashboard%20Tutorial.mp4";

function StudentDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [modules, setModules] = useState([]);
  const [classes, setClasses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [progressSummary, setProgressSummary] = useState({ total_lessons: 0, completed_lessons: 0, percentage: 0 });
  const [joinCode, setJoinCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [badges, setBadges] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [dueDates, setDueDates] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [moduleLessons, setModuleLessons] = useState({}); // Map of module_id -> lesson_ids
  const [parentAccessLink, setParentAccessLink] = useState(null);
  const [showParentShareModal, setShowParentShareModal] = useState(false);
  // Parent-share email form (NEW — production-readiness phase)
  const [parentEmailDraft, setParentEmailDraft] = useState({ email: "", name: "", message: "" });
  const [sendingParentEmail, setSendingParentEmail] = useState(false);
  const [parentEmailStatus, setParentEmailStatus] = useState(null); // null | "sent" | "failed"
  const [parentLinkCopied, setParentLinkCopied] = useState(false);
  const [creatingParentLink, setCreatingParentLink] = useState(false);
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false);
  // ---- Personalization Phase 1 ----
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profile, setProfile] = useState(user);
  // Keep profile in sync if parent passes a new user
  useEffect(() => {
    setProfile(user);
  }, [user]);
  const welcomeVideoRef = useRef(null);
  const [capstoneFields, setCapstoneFields] = useState({});

  // Check for first-time user on mount
  useEffect(() => {
    const hasSeenQuickStart = localStorage.getItem("quickStartCompleted");
    if (!hasSeenQuickStart) {
      setShowQuickStart(true);
    }
  }, []);

  // Load data on mount and whenever we return to the dashboard
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, i18n.language]);

  const loadData = async () => {
    try {
      const lang = i18n.language || 'en';
      const [modulesRes, classesRes, progressRes, progressSummaryRes, badgesRes] = await Promise.all([
        axios.get(`${API}/modules?learning_path=standard&lang=${lang}`),
        axios.get(`${API}/classes`),
        axios.get(`${API}/progress/my`),
        axios.get(`${API}/progress/me?learning_path=standard`),
        axios.get(`${API}/gamification/badges`)
      ]);
      setModules(modulesRes.data);
      setClasses(classesRes.data);
      setProgress(progressRes.data);
      setProgressSummary(progressSummaryRes.data);
      setAllBadges(badgesRes.data);
      setBadges(user.badges || []);
      
      // Load lessons for each module to properly calculate completion
      const lessonsMap = {};
      for (const mod of modulesRes.data) {
        try {
          const lessonsRes = await axios.get(`${API}/modules/${mod.id}/lessons?lang=${lang}`);
          lessonsMap[mod.id] = lessonsRes.data.map(l => l.id);
        } catch (err) {
          lessonsMap[mod.id] = [];
        }
      }
      setModuleLessons(lessonsMap);
      
      // Load capstone progress
      try {
        const capRes = await axios.get(`${API}/capstone/progress`);
        setCapstoneFields(capRes.data?.fields || {});
      } catch (e) { /* ignore */ }
      
      // Load due dates and announcements for all enrolled classes
      if (classesRes.data.length > 0) {
        try {
          // Fetch from all classes and merge
          const allDueDates = [];
          const allAnnouncements = [];
          
          for (const cls of classesRes.data) {
            try {
              const [dueDatesRes, announcementsRes] = await Promise.all([
                axios.get(`${API}/classes/${cls.id}/due-dates`),
                axios.get(`${API}/classes/${cls.id}/announcements`)
              ]);
              allDueDates.push(...(dueDatesRes.data || []));
              allAnnouncements.push(...(announcementsRes.data || []));
            } catch (err) {
              // Skip if this class has no announcements/due dates
            }
          }
          
          setDueDates(allDueDates);
          // Sort announcements by date, most recent first
          setAnnouncements(allAnnouncements.sort((a, b) => 
            new Date(b.created_at) - new Date(a.created_at)
          ));
        } catch (err) {
          console.log("No due dates or announcements available");
        }
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClass = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/classes/join`, { join_code: joinCode });
      setJoinCode("");
      setShowJoinModal(false);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to join class");
    }
  };

  const getProgressPercent = () => {
    return progressSummary.percentage || 0;
  };

  // Calculate completed module numbers
  const getCompletedModules = () => {
    const completedIds = progress.filter(p => p.completed).map(p => p.lesson_id);
    const completed = [];
    modules.forEach(mod => {
      const modLessons = moduleLessons[mod.id] || [];
      if (modLessons.length > 0 && modLessons.every(lid => completedIds.includes(lid))) {
        completed.push(mod.order);
      }
    });
    return completed;
  };

  // Get certification level
  const getCertLevel = () => {
    const cm = getCompletedModules();
    if (cm.includes(7) && cm.length === 7) return { level: 3, title: "Future CEO", color: "from-yellow-400 to-amber-500" };
    if ([4,5,6].every(n => cm.includes(n))) return { level: 2, title: "Business Builder", color: "from-blue-400 to-indigo-500" };
    if ([1,2,3].every(n => cm.includes(n))) return { level: 1, title: "Entrepreneur", color: "from-green-400 to-emerald-500" };
    return null;
  };

  const getBadgeDetails = (badgeId) => {
    return allBadges.find(b => b.id === badgeId);
  };

  const getDueDateForSession = (sessionNumber) => {
    const dd = dueDates.find(d => d.session_number === sessionNumber);
    return dd ? new Date(dd.due_date) : null;
  };

  const isPastDue = (sessionNumber) => {
    const dd = getDueDateForSession(sessionNumber);
    return dd && dd < new Date();
  };

  // Switch to Middle School learning path
  const switchToMiddleSchool = async () => {
    try {
      await axios.put(`${API}/auth/profile`, { learning_path: "middle_school" });
      localStorage.setItem("preferred_learning_path", "middle_school");
    } catch (e) { /* ignore */ }
    // Hard reload so App's user state reflects new learning_path
    window.location.href = "/middle-school";
  };

  // Parent Access Functions
  const checkParentAccessStatus = async () => {
    try {
      const response = await axios.get(`${API}/parent-access/status`);
      if (response.data.has_parent_access_link) {
        setParentAccessLink(response.data.share_token);
      }
    } catch (err) {
      console.log("No parent access link yet");
    }
  };

  const createParentAccessLink = async () => {
    try {
      setCreatingParentLink(true);
      const response = await axios.post(`${API}/parent-access/create`);
      setParentAccessLink(response.data.share_token);
      setShowParentShareModal(true);
    } catch (err) {
      console.error("Error creating parent access link:", err);
    } finally {
      setCreatingParentLink(false);
    }
  };

  const copyParentLink = () => {
    const link = `${window.location.origin}/parent/${parentAccessLink}`;
    navigator.clipboard.writeText(link);
    setParentLinkCopied(true);
    setTimeout(() => setParentLinkCopied(false), 2000);
  };

  // Check parent access status on mount
  useEffect(() => {
    if (user) {
      checkParentAccessStatus();
    }
  }, [user]);

  // Download the student's own Mini Business Plan PDF (auth-protected endpoint)
  const downloadMyBusinessPlan = async () => {
    try {
      const res = await axios.get(`${API}/student/business-plan/pdf`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = ((profile?.name || "Student").split(" ")[0] || "Student").replace(/\s+/g, "_");
      a.download = `${safe}_Business_Plan.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("Error downloading business plan:", err);
      alert("Couldn't download your Business Plan PDF. Please try again.");
    }
  };

  // Email the Parent View link to a parent (NEW — production-readiness phase)
  const sendParentShareEmail = async () => {
    setParentEmailStatus(null);
    setSendingParentEmail(true);
    try {
      const res = await axios.post(
        `${API}/parent-access/email`,
        {
          parent_email: parentEmailDraft.email.trim(),
          parent_name: parentEmailDraft.name.trim() || null,
          student_message: parentEmailDraft.message.trim() || null,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (res.data?.sent) {
        setParentEmailStatus("sent");
        setParentEmailDraft({ email: "", name: "", message: "" });
      } else {
        setParentEmailStatus("failed");
      }
    } catch (err) {
      console.error("parent-share email failed:", err);
      setParentEmailStatus("failed");
    } finally {
      setSendingParentEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="text-4xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="lemon-button px-6 py-2 rounded-lg text-white font-semibold"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8">
        <AnnouncementBanner role={user?.role} />
        {/* Course Completion Banner */}
        {progressSummary.completed_lessons >= progressSummary.total_lessons && progressSummary.total_lessons > 0 && (
          <div className="bg-gradient-to-r from-green-400 to-blue-500 rounded-2xl shadow-2xl p-8 mb-8 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="text-6xl">🎉</div>
                <div>
                  <h2 className="text-3xl font-bold mb-2">{t('completion.congratulations')}</h2>
                  <p className="text-lg">{t('dashboard.completedAllLessons')}</p>
                </div>
              </div>
              <button
                onClick={() => navigate("/completion")}
                className="bg-white text-green-600 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                🏆 {t('completion.viewCertificate')}
              </button>
            </div>
          </div>
        )}

        {/* Welcome Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8">
          <div className="flex items-center gap-4 sm:gap-5 flex-wrap">
            {(() => {
              const av = (profile?.avatar_url || "").replace(/^preset:/, "");
              const emoji = AVATAR_EMOJI[av];
              return (
                <button
                  type="button"
                  onClick={() => setShowProfileModal(true)}
                  data-testid="dashboard-avatar-btn"
                  aria-label="Edit your profile"
                  className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-4xl sm:text-5xl border-2 border-gray-200 hover:border-amber-400 transition-colors shadow-sm shrink-0"
                  style={{ background: emoji ? AVATAR_BG[av] : "#F3F4F6" }}
                >
                  {emoji ? (
                    <span aria-hidden="true">{emoji}</span>
                  ) : (
                    <span className="text-2xl text-gray-400" aria-hidden="true">+</span>
                  )}
                  <span className="absolute -bottom-1 -right-1 bg-white border border-gray-200 rounded-full w-6 h-6 flex items-center justify-center shadow-sm">
                    <Pencil className="w-3 h-3 text-gray-500" aria-hidden="true" />
                  </span>
                </button>
              );
            })()}

            <div className="flex-1 min-w-[200px]">
              <h1 className="text-2xl sm:text-4xl font-bold text-gray-800 leading-tight" data-testid="welcome-message">
                {t('dashboard.welcomeBack')}, {(profile?.name || "").split(" ")[0]}! 🍋
              </h1>
              {profile?.business_name ? (
                <p
                  className="mt-1 text-base sm:text-lg font-semibold text-amber-700"
                  data-testid="dashboard-business-name"
                >
                  {t('dashboard.ceoOf')} <span className="text-pink-600">{profile.business_name}</span>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowProfileModal(true)}
                  className="mt-1 text-sm font-semibold text-amber-700 underline hover:text-amber-800"
                  data-testid="dashboard-set-business-name"
                >
                  {t('dashboard.addBusinessName')}
                </button>
              )}
              {profile?.team_role && TEAM_ROLE_META[profile.team_role] && (() => {
                const meta = TEAM_ROLE_META[profile.team_role];
                const Icon = meta.icon;
                return (
                  <span
                    className={`mt-2 inline-flex items-center gap-1 text-xs font-bold border rounded-full px-2 py-0.5 ${meta.color}`}
                    data-testid="dashboard-role-badge"
                  >
                    <Icon className="w-3 h-3" aria-hidden="true" />
                    {meta.label}
                  </span>
                );
              })()}
              <p className="mt-2 text-sm text-gray-500">{t('dashboard.continueJourney')}</p>
            </div>

            <div className="flex gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-3xl sm:text-5xl font-bold text-yellow-600">{progressSummary.completed_lessons}</div>
                <div className="text-xs sm:text-sm text-gray-600">{t('dashboard.lessonsCompleted')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-5xl font-bold text-green-600">{profile?.points || 0}</div>
                <div className="text-xs sm:text-sm text-gray-600">{t('dashboard.pointsEarned')}</div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">{t('dashboard.overallProgress')}</span>
              <span className="text-sm font-semibold text-gray-700" data-testid="dashboard-progress-text">
                {progressSummary.completed_lessons} / {progressSummary.total_lessons} ({getProgressPercent()}%)
              </span>
            </div>
            <div
              className="w-full bg-gray-200 rounded-full h-4"
              role="progressbar"
              aria-valuenow={getProgressPercent()}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Overall progress ${getProgressPercent()}%`}
              data-testid="dashboard-progress-bar"
            >
              <div
                className="progress-bar h-4 rounded-full transition-all duration-500"
                style={{ width: `${getProgressPercent()}%` }}
              ></div>
            </div>
          </div>

          {/* Middle School Path Link */}
          <div className="mt-4 text-center">
            <button
              onClick={switchToMiddleSchool}
              className="text-sm text-purple-600 hover:text-purple-800 font-medium hover:underline inline-flex items-center gap-1"
              data-testid="switch-to-middle-school-link"
            >
              {t('dashboard.youngerStudentCta')}
            </button>
          </div>

          {/* Badges Display */}
          {badges.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('dashboard.recentBadges')}</h3>
              <div className="flex flex-wrap gap-2">
                {badges.slice(0, 5).map((badgeId) => {
                  const badge = getBadgeDetails(badgeId);
                  const badgeName = t(`badges.${badgeId}.name`, { defaultValue: badge?.name });
                  const badgeDesc = t(`badges.${badgeId}.description`, { defaultValue: badge?.description });
                  return badge ? (
                    <div
                      key={badgeId}
                      className="flex items-center gap-2 bg-yellow-100 border-2 border-yellow-400 rounded-full px-3 py-1"
                      title={badgeDesc}
                    >
                      <span className="text-xl">{badge.icon}</span>
                      <span className="text-xs font-semibold text-yellow-800">{badgeName}</span>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          )}
        </div>

        {/* CEO Challenge — Youth CEO Camp Final Challenge tile */}
        <div className="bg-gradient-to-br from-[#0B1E3F] to-[#132A54] text-white rounded-3xl shadow-xl p-6 sm:p-8 mb-8 relative overflow-hidden" data-testid="dashboard-ceo-challenge-tile">
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#FFD500]/25 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-20 -left-16 w-52 h-52 rounded-full bg-[#7C3AED]/20 blur-3xl" aria-hidden="true" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-[#FFD500] text-black flex items-center justify-center text-3xl font-black shadow-lg shrink-0" aria-hidden="true">
              🏆
            </div>
            <div className="flex-1">
              <p className="text-[11px] font-black uppercase tracking-widest text-[#FFD500] mb-1">Youth CEO Camp</p>
              <h3 className="text-xl sm:text-2xl font-black leading-tight">Final Challenge — Are You Pitch Day Ready?</h3>
              <p className="mt-1.5 text-sm text-white/70">
                20-question quiz on entrepreneurship, branding, money, pitching &amp; confidence. Earn the Youth CEO Camp Graduate badge + certificate.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <button
                onClick={() => navigate("/ceo-challenge")}
                data-testid="dashboard-ceo-challenge-start"
                className="inline-flex items-center justify-center gap-2 bg-[#FFD500] text-black font-black rounded-full px-5 py-3 hover:bg-white transition-colors"
              >
                Start Challenge →
              </button>
              <button
                onClick={() => navigate("/ceo-challenge/leaderboard")}
                data-testid="dashboard-ceo-challenge-leaderboard"
                className="inline-flex items-center justify-center gap-2 bg-white/10 ring-1 ring-white/25 text-white text-sm font-bold rounded-full px-4 py-3 hover:bg-white/15 transition-colors"
              >
                Leaderboard
              </button>
            </div>
          </div>
        </div>

        {/* Class Announcements */}
        {announcements.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8" data-testid="student-announcements">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span>📢</span> {t('dashboard.classAnnouncements')}
            </h2>
            <div className="space-y-4">
              {announcements.slice(0, 3).map((announcement) => (
                <div
                  key={announcement.id}
                  className="border-l-4 border-blue-400 bg-blue-50 rounded-r-lg p-4"
                  data-testid={`student-announcement-${announcement.id}`}
                >
                  <h3 className="font-bold text-gray-800 mb-1">{announcement.title}</h3>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{announcement.content}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {announcement.created_by_name} • {new Date(announcement.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bonus Resources Banner */}
        <div className="mb-8">
          <div className="bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl shadow-xl p-8 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="text-6xl">🎁</div>
                <div>
                  <h2 className="text-3xl font-bold mb-2">{t('dashboard.bonusResources')}</h2>
                  <p className="text-lg opacity-90">
                    {t('dashboard.bonusDescription')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate("/bonus")}
                className="bg-white text-purple-600 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                {t('dashboard.viewBonusContent')} →
              </button>
            </div>
          </div>
        </div>

        {/* Solo Practice Mode Banner */}
        <div className="mb-8" data-testid="solo-practice-section">
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl shadow-xl p-8 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="text-6xl">🎮</div>
                <div>
                  <h2 className="text-3xl font-bold mb-2">Solo Practice Mode</h2>
                  <p className="text-lg opacity-90">
                    Play through game modules at your own pace and earn your AI Business Plan!
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate("/game/profile")}
                  data-testid="game-profile-btn"
                  className="bg-white/20 hover:bg-white/30 text-white px-6 py-4 rounded-xl font-bold text-lg transition-all"
                >
                  📊 My Progress
                </button>
                <button
                  onClick={() => navigate("/game/solo")}
                  data-testid="solo-practice-btn"
                  className="bg-white text-orange-600 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                >
                  🚀 Start Solo Mode →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Share with Parents Banner */}
        <div className="mb-8" data-testid="share-with-parents-section">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl shadow-xl p-8 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="text-6xl">👨‍👩‍👧</div>
                <div>
                  <h2 className="text-3xl font-bold mb-2">{t('dashboard.shareWithParents', 'Share with Parents')}</h2>
                  <p className="text-lg opacity-90">
                    {t('dashboard.shareWithParentsDesc', 'Let your parents see your amazing progress and business plan!')}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={downloadMyBusinessPlan}
                  className="bg-white/15 hover:bg-white/25 text-white px-5 py-3 rounded-xl font-bold text-sm shadow-md transition-all flex items-center gap-2 border border-white/30"
                  data-testid="dashboard-download-my-plan-btn"
                >
                  <Download className="w-4 h-4" />
                  {t('dashboard.downloadMyPlan', 'Download My Plan PDF')}
                </button>
                {parentAccessLink ? (
                  <button
                    onClick={() => setShowParentShareModal(true)}
                    className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all flex items-center gap-2"
                    data-testid="dashboard-view-share-link-btn"
                  >
                    <Share2 className="w-5 h-5" />
                    {t('dashboard.viewShareLink', 'View Share Link')}
                  </button>
                ) : (
                  <button
                    onClick={createParentAccessLink}
                    disabled={creatingParentLink}
                    className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-50"
                    data-testid="dashboard-create-share-link-btn"
                  >
                    <Share2 className="w-5 h-5" />
                    {creatingParentLink ? t('common.creating', 'Creating...') : t('dashboard.createShareLink', 'Create Share Link')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Parent Share Modal */}
        {showParentShareModal && parentAccessLink && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="text-center mb-6">
                <div className="text-5xl mb-3">👨‍👩‍👧</div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {t('dashboard.shareWithParentsTitle', 'Share Your Progress!')}
                </h2>
                <p className="text-gray-600 mt-2">
                  {t('dashboard.shareWithParentsInfo', 'Send this link to your parents so they can see your amazing work!')}
                </p>
              </div>

              <div className="bg-gray-100 rounded-xl p-4 mb-4">
                <p className="text-xs text-gray-500 mb-2">{t('dashboard.parentAccessLink', 'Parent Access Link')}</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/parent/${parentAccessLink}`}
                    className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700"
                  />
                  <button
                    onClick={copyParentLink}
                    className={`p-2 rounded-lg transition-colors ${
                      parentLinkCopied 
                        ? 'bg-green-500 text-white' 
                        : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                    }`}
                  >
                    {parentLinkCopied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="bg-indigo-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-indigo-800">
                  <strong>{t('dashboard.parentsCanSee', 'Parents can see')}:</strong>
                </p>
                <ul className="text-sm text-indigo-700 mt-2 space-y-1">
                  <li>✓ {t('dashboard.parentViewProgress', 'Your progress through the program')}</li>
                  <li>✓ {t('dashboard.parentViewPlan', 'Your AI Business Plan')}</li>
                  <li>✓ {t('dashboard.parentViewCert', 'Your Certificate (when complete)')}</li>
                  <li>✓ {t('dashboard.parentViewWorkbook', 'Your Workbook reflections')}</li>
                </ul>
              </div>

              {/* Email this to a parent */}
              <div
                className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6"
                data-testid="parent-share-email-form"
              >
                <p className="text-sm font-bold text-amber-800 mb-2">
                  📧 {t('parentView.emailToParent', "Email this to a parent")}
                </p>
                <p className="text-xs text-amber-700 mb-3">
                  {t('parentView.emailModalDesc', "We'll send a friendly email with your Parent View link and PDF.")}
                </p>
                <div className="space-y-2">
                  <input
                    type="email"
                    value={parentEmailDraft.email}
                    onChange={(e) => setParentEmailDraft((d) => ({ ...d, email: e.target.value }))}
                    placeholder={t('parentView.parentEmailLabel', "Parent's email")}
                    aria-label={t('parentView.parentEmailLabel', "Parent's email")}
                    disabled={sendingParentEmail}
                    className="w-full px-3 py-2 rounded-lg border border-amber-300 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 text-sm bg-white"
                    data-testid="parent-share-email-input"
                  />
                  <input
                    type="text"
                    value={parentEmailDraft.name}
                    onChange={(e) => setParentEmailDraft((d) => ({ ...d, name: e.target.value }))}
                    placeholder={t('parentView.parentNameLabel', "Parent's name (optional)")}
                    aria-label={t('parentView.parentNameLabel', "Parent's name")}
                    disabled={sendingParentEmail}
                    maxLength={80}
                    className="w-full px-3 py-2 rounded-lg border border-amber-300 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 text-sm bg-white"
                    data-testid="parent-share-name-input"
                  />
                  <textarea
                    value={parentEmailDraft.message}
                    onChange={(e) => setParentEmailDraft((d) => ({ ...d, message: e.target.value }))}
                    placeholder={t('parentView.messageLabel', "Add a short note (optional)")}
                    aria-label={t('parentView.messageLabel', "Add a short note")}
                    disabled={sendingParentEmail}
                    maxLength={300}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-amber-300 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 text-sm bg-white resize-none"
                    data-testid="parent-share-message-input"
                  />
                  <button
                    type="button"
                    onClick={sendParentShareEmail}
                    disabled={sendingParentEmail || !parentEmailDraft.email}
                    className="w-full px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="parent-share-send-btn"
                  >
                    {sendingParentEmail
                      ? t('parentView.sending', 'Sending…')
                      : t('parentView.sendEmail', 'Send email')}
                  </button>
                  {parentEmailStatus === "sent" && (
                    <p
                      className="text-xs text-emerald-700 font-semibold flex items-center gap-1"
                      role="status"
                      data-testid="parent-share-email-success"
                    >
                      <Check className="w-4 h-4" /> {t('parentView.emailSent', 'Email sent! 🎉')}
                    </p>
                  )}
                  {parentEmailStatus === "failed" && (
                    <p
                      className="text-xs text-red-700 font-semibold"
                      role="alert"
                      data-testid="parent-share-email-error"
                    >
                      {t('parentView.emailFailed', "We couldn't send the email. Please try again or copy the link.")}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowParentShareModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-semibold transition-colors"
                >
                  {t('common.close', 'Close')}
                </button>
                <a
                  href={`${window.location.origin}/parent/${parentAccessLink}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  {t('dashboard.previewLink', 'Preview')}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Classes Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">{t('dashboard.myClasses')}</h2>
            <button
              onClick={() => setShowJoinModal(true)}
              className="lemon-button px-6 py-2 rounded-lg text-white font-semibold"
              data-testid="join-class-btn"
            >
              + {t('dashboard.joinClass')}
            </button>
          </div>

          {classes.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon" aria-hidden="true">🏫</div>
              <h3 className="empty-state-title">{t('dashboard.noClassesYet')}</h3>
              <p className="empty-state-description">{t('dashboard.joinFirstClass')}</p>
              <button
                onClick={() => setShowJoinModal(true)}
                className="lemon-button px-6 py-3 rounded-lg text-white font-semibold mt-4"
                aria-label="Join your first class"
              >
                {t('dashboard.joinClass')}
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {classes.map((cls) => (
                <div
                  key={cls.id}
                  className="bg-white rounded-2xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition-all"
                  onClick={() => navigate(`/classes/${cls.id}`)}
                  data-testid={`class-card-${cls.id}`}
                >
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{cls.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{cls.description || t('common.noDescription')}</p>
                  <div className="flex items-center text-sm text-gray-500">
                    <span>👥 {cls.student_count} {t('common.students')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Capstone Tracker */}
        <CapstoneTracker completedModules={getCompletedModules()} capstoneFields={capstoneFields} />

        {/* Certification Level Badge */}
        {getCertLevel() && (
          <div className={`bg-gradient-to-r ${getCertLevel().color} rounded-2xl shadow-lg p-6 mb-8 text-white`} data-testid="cert-level-badge">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center text-3xl font-bold backdrop-blur-sm">
                L{getCertLevel().level}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider opacity-80">Certification Level {getCertLevel().level}</div>
                <div className="text-2xl font-bold">{getCertLevel().title}</div>
              </div>
            </div>
          </div>
        )}

        {/* Welcome Tutorial Video */}
        <div className="mb-8">
          <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-2xl p-6 border-2 border-yellow-300 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="text-4xl">🎬</span>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    {t('dashboard.welcomeVideo', { defaultValue: 'Welcome to Lemonade Stand!' })}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {t('dashboard.welcomeVideoDescription', { defaultValue: 'Watch this quick tutorial to learn how to use the app' })}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowWelcomeVideo(!showWelcomeVideo)}
                className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-full font-semibold transition-all shadow-md hover:shadow-lg"
              >
                <Play className="w-5 h-5" />
                {showWelcomeVideo ? t('common.hide', { defaultValue: 'Hide' }) : t('common.watch', { defaultValue: 'Watch' })}
              </button>
            </div>
            
            {showWelcomeVideo && (
              <div className="mt-4 rounded-xl overflow-hidden shadow-lg">
                <VideoPlayer
                  key="welcome-video"
                  src={WELCOME_VIDEO_URL}
                  videoRef={welcomeVideoRef}
                  autoPlay={false}
                />
              </div>
            )}
          </div>
        </div>

        {/* Modules Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('dashboard.learningModules')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module, index) => {
              const lessonIds = moduleLessons[module.id] || [];
              const completedLessons = progress.filter(p => 
                lessonIds.includes(p.lesson_id) && p.completed
              );
              const isModuleComplete = lessonIds.length > 0 && completedLessons.length === lessonIds.length;
              const isInProgress = completedLessons.length > 0 && !isModuleComplete;
              const dueDate = getDueDateForSession(module.order);
              const pastDue = isPastDue(module.order);
              
              const sessionTitle = t(`sessions.${module.order}.title`, { defaultValue: module.title });
              const sessionDesc = t(`sessions.${module.order}.description`, { defaultValue: module.description });
              
              return (
                <div
                  key={module.id}
                  className={`module-card rounded-2xl shadow-lg p-6 cursor-pointer ${pastDue ? 'ring-2 ring-red-400' : ''} ${isModuleComplete ? 'ring-2 ring-green-400' : ''}`}
                  onClick={() => navigate(`/lessons/${module.id}`)}
                  data-testid={`module-card-${index}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="text-3xl">{isModuleComplete ? '✅' : '🍋'}</div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-sm font-bold text-yellow-700 bg-yellow-200 px-3 py-1 rounded-full">
                        {t('common.session')} {module.order}
                      </span>
                      {pastDue && !isModuleComplete && (
                        <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full" data-testid={`past-due-${module.order}`}>
                          {t('dashboard.pastDue')}
                        </span>
                      )}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{sessionTitle}</h3>
                  <p className="text-gray-600 text-sm mb-4">{sessionDesc}</p>
                  {dueDate && !pastDue && !isModuleComplete && (
                    <div className="text-xs text-gray-500 mb-2">
                      {t('dashboard.due')}: {dueDate.toLocaleDateString()}
                    </div>
                  )}
                  {isModuleComplete ? (
                    <div className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                      ✓ {t('dashboard.complete')}
                    </div>
                  ) : isInProgress ? (
                    <div className="inline-block px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold">
                      {completedLessons.length}/{lessonIds.length} {t('dashboard.lessons')}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Join Class Modal */}
      {showJoinModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
          onClick={() => setShowJoinModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="join-class-title"
        >
          <div 
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 animate-slide-up" 
            onClick={(e) => e.stopPropagation()}
            role="document"
          >
            <h2 id="join-class-title" className="text-2xl font-bold text-gray-800 mb-4">Join a Class</h2>
            <p className="text-gray-600 mb-6">Enter the class code provided by your educator</p>
            <form onSubmit={handleJoinClass}>
              <label htmlFor="join-code" className="sr-only">Class join code</label>
              <input
                id="join-code"
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200 mb-4 text-center text-2xl font-bold tracking-wider"
                placeholder="ABC123"
                maxLength="6"
                required
                autoFocus
                autoComplete="off"
                data-testid="join-code-input"
                aria-describedby="join-code-hint"
              />
              <p id="join-code-hint" className="sr-only">Enter the 6-character class code</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowJoinModal(false)}
                  className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 focus:ring-2 focus:ring-gray-300"
                  data-testid="cancel-join-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 lemon-button px-4 py-3 rounded-lg text-white font-semibold"
                  data-testid="submit-join-btn"
                >
                  Join Class
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Start Guide for first-time users */}
      {showQuickStart && (
        <QuickStartGuide 
          onClose={() => setShowQuickStart(false)} 
          userName={user?.name}
        />
      )}
      <LemonBotFloater activity="student_dashboard" />

      {/* Personalization Phase 1: Student Profile editor */}
      <StudentProfileModal
        open={showProfileModal}
        user={profile}
        onClose={() => setShowProfileModal(false)}
        onSaved={(updated) => setProfile((p) => ({ ...(p || {}), ...updated }))}
      />
    </div>
  );
}

export default StudentDashboard;
